/// <reference path="../../../types/axios-fix.d.ts" />
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { HttpClientService } from './http-client.service';
import { AuthInterceptorService } from './auth-interceptor.service';
import { ErrorHandlerService, ApiResponse } from './error-handler.service';
import { CacheService } from './cache.service';
import { ErrorCode } from '../error-handling';

/**
 * Base Service Class
 * 
 * Provides a foundation for all domain services by combining core infrastructure:
 * - HTTP client for requests
 * - Authentication handling
 * - Error handling and logging
 * - Request caching
 * - Response data extraction
 * 
 * All domain services should extend this base class.
 */
export abstract class BaseService {
  protected httpClient: HttpClientService;
  protected authInterceptor: AuthInterceptorService;
  protected errorHandler: ErrorHandlerService;
  protected cache: CacheService;

  constructor(
    httpClient: HttpClientService,
    authInterceptor: AuthInterceptorService,
    errorHandler: ErrorHandlerService,
    cache: CacheService
  ) {
    this.httpClient = httpClient;
    this.authInterceptor = authInterceptor;
    this.errorHandler = errorHandler;
    this.cache = cache;
  }

  /**
   * Make a cached GET request
   * Matches the pattern from the monolithic service
   */
  protected async cachedRequest<T>(
    url: string,
    params?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    // Check cache first
    const cached = this.cache.get<T>(url, params);
    if (cached) {
      return cached;
    }

    // Check for pending request
    const pending = this.cache.getPendingRequest<T>(url, params);
    if (pending) {
      console.log(`Waiting for pending request: ${url}`);
      return pending;
    }

    // Make the request
    const requestPromise = this.makeRequest<T>(url, params, config);
    this.cache.setPendingRequest(url, requestPromise, params);

    try {
      const result = await requestPromise;
      this.cache.set(url, result, params);
      return result;
    } catch (error) {
      // Log the error and reject the promise with the handled error
      const handledError = this.errorHandler.handleError(error, `BaseService.cachedRequest(${url})`);
      return Promise.reject(handledError);
    }
  }

  /**
   * Make a direct request without caching
   */
  protected async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      let response: AxiosResponse;

      switch (method) {
        case 'GET':
          response = await this.httpClient.get(url, config);
          break;
        case 'POST':
          response = await this.httpClient.post(url, data, config);
          break;
        case 'PUT':
          response = await this.httpClient.put(url, data, config);
          break;
        case 'PATCH':
          response = await this.httpClient.patch(url, data, config);
          break;
        case 'DELETE':
          response = await this.httpClient.delete(url, config);
          break;
        default:
          // Create an ApiError for unsupported method and reject the promise instead of throwing
          const methodError = this.errorHandler.createError(
            ErrorCode.INVALID_REQUEST,
            `Unsupported HTTP method: ${method}`
          );
          this.errorHandler.logError(methodError, `BaseService.${method}`);
          return Promise.reject(methodError);
      }

      return this.errorHandler.extractResponseData<T>(response);
    } catch (error) {
      // Log the error and reject the promise with the handled error
      const handledError = this.errorHandler.handleError(error, `BaseService.${method}(${url})`);
      return Promise.reject(handledError);
    }
  }

  /**
   * Make a request and return full response with metadata
   */
  protected async requestWithMetadata<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      let response: AxiosResponse;

      switch (method) {
        case 'GET':
          response = await this.httpClient.get(url, config);
          break;
        case 'POST':
          response = await this.httpClient.post(url, data, config);
          break;
        case 'PUT':
          response = await this.httpClient.put(url, data, config);
          break;
        case 'PATCH':
          response = await this.httpClient.patch(url, data, config);
          break;
        case 'DELETE':
          response = await this.httpClient.delete(url, config);
          break;
        default:
          // Create an ApiError for unsupported method and reject the promise instead of throwing
          const methodError = this.errorHandler.createError(
            ErrorCode.INVALID_REQUEST,
            `Unsupported HTTP method: ${method}`
          );
          this.errorHandler.logError(methodError, `BaseService.${method}WithMetadata`);
          return Promise.reject(methodError);
      }

      return this.errorHandler.extractResponseWithMetadata<T>(response);
    } catch (error) {
      // Log the error and reject the promise with the handled error
      const handledError = this.errorHandler.handleError(error, `BaseService.${method}WithMetadata(${url})`);
      return Promise.reject(handledError);
    }
  }

  /**
   * Internal method to make the actual request for cached requests
   */
  private async makeRequest<T>(
    url: string,
    params?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.httpClient.get(url, {
        ...config,
        params,
        timeout: config?.timeout || 10000
      });

      return this.errorHandler.extractResponseData<T>(response);
    } catch (error) {
      // Handle the error and reject the promise with a properly formatted ApiError
      const handledError = this.errorHandler.handleError(error, `BaseService.makeRequest(${url})`);
      return Promise.reject(handledError);
    }
  }

  /**
   * Invalidate cache based on operation type
   */
  protected invalidateCache(type: 'user' | 'organization' | 'team' | 'content' | 'search' | 'rbac' | 'subscription', id?: string): void {
    switch (type) {
      case 'user':
        this.cache.invalidateUserCache();
        break;
      case 'organization':
        this.cache.invalidateOrganizationCache(id);
        break;
      case 'team':
        this.cache.invalidateTeamCache(id);
        break;
      case 'content':
        this.cache.invalidateContentCache();
        break;
      case 'search':
        this.cache.invalidateSearchCache();
        break;
      case 'subscription':
        this.cache.invalidateSubscriptionCache();
        break;
      case 'rbac':
        // Invalidate RBAC-related cache entries manually
        const keysToDelete = Array.from((this.cache as any).cache.keys()).filter((key: string) => 
          key.includes('/rbac') || key.includes('/roles') || key.includes('/permissions')
        );
        keysToDelete.forEach((key: string) => (this.cache as any).cache.delete(key));
        break;
    }
  }

  /**
   * GET request helper
   */
  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>('GET', url, undefined, config);
  }

  /**
   * POST request helper
   */
  protected async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>('POST', url, data, config);
  }

  /**
   * PUT request helper
   */
  protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>('PUT', url, data, config);
  }

  /**
   * PATCH request helper
   */
  protected async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>('PATCH', url, data, config);
  }

  /**
   * DELETE request helper
   */
  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>('DELETE', url, undefined, config);
  }

  /**
   * Cached GET request helper
   */
  protected async getCached<T>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.cachedRequest<T>(url, params, config);
  }
}

// Create shared instances of core services
const httpClientService = new HttpClientService();
const authInterceptorService = new AuthInterceptorService(httpClientService);
const errorHandlerService = new ErrorHandlerService();
const cacheService = new CacheService();

/**
 * Factory function to create base service instances with shared infrastructure
 */
export function createBaseService(): BaseService {
  return new (class extends BaseService {
    constructor() {
      super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
    }
  })();
}

// Export the shared service instances for direct use
export {
  httpClientService,
  authInterceptorService,
  errorHandlerService,
  cacheService
};